import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADDON_UPLOAD_KIND_LABELS, type AddonUploadSource, type AddonUploadView } from '@thottbot/shared';
import { apiGet, apiSend } from '../../api/client';
import { relativeTime } from '../../util/time';
import { sourceLabel } from '../Me';
import { describeError } from '../Submit';

interface AdminUpload extends AddonUploadView {
  uploaderBattletag: string;
  uploaderId: string;
  artifactCount: number;
}
interface Settings {
  minVersion: string;
}

const PAGE = 50;

/** The "Show" filter: by provenance or by file kind; the API takes `source` or `kind`. */
type Filter = '' | AddonUploadSource | 'kind:addon' | 'kind:cache';
function filterQuery(filter: Filter): string {
  if (!filter) return '';
  return filter.startsWith('kind:') ? `&kind=${filter.slice(5)}` : `&source=${filter}`;
}

export function AddonUploads() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [source, setSource] = useState<Filter>('');
  const [minVersion, setMinVersion] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['admin', 'addon', 'settings'],
    queryFn: () => apiGet<Settings>('/api/admin/addon/settings'),
  });
  useEffect(() => {
    if (settings.data) setMinVersion(settings.data.minVersion);
  }, [settings.data]);
  const uploads = useQuery({
    queryKey: ['admin', 'addon', 'uploads', page, source],
    queryFn: () =>
      apiGet<{ page: number; total: number; items: AdminUpload[] }>(
        `/api/admin/addon/uploads?page=${page}${filterQuery(source)}`,
      ),
    refetchInterval: (q) => (q.state.data?.items.some((u) => u.status === 'processing') ? 2000 : false),
    refetchIntervalInBackground: true,
  });

  const save = useMutation({
    mutationFn: (value: string) => apiSend<Settings>('PUT', '/api/admin/addon/settings', { minVersion: value }),
    onSuccess: (data) => {
      setError(null);
      setSaved(`Minimum addon version is now ${data.minVersion}`);
      qc.invalidateQueries({ queryKey: ['admin', 'addon', 'settings'] });
      qc.invalidateQueries({ queryKey: ['addon', 'info'] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const revert = useMutation({
    mutationFn: (id: string) => apiSend<AddonUploadView>('POST', `/api/admin/addon/uploads/${id}/revert`),
    onSuccess: (data) => {
      setError(null);
      setConfirming(null);
      const r = data.counts.revert;
      setNotice(r ? `${r.recomputed} artifacts recomputed, ${r.removed} removed` : 'Upload reverted');
      qc.invalidateQueries({ queryKey: ['admin', 'addon', 'uploads'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  return (
    <div data-testid="admin-addon">
      <form
        className="section"
        aria-label="Addon settings"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(minVersion.trim());
        }}
      >
        <div className="form-row">
          <label htmlFor="addon-min-version">Minimum addon version</label>
          <input
            id="addon-min-version"
            type="text"
            value={minVersion}
            pattern="\d+\.\d+\.\d+"
            title="major.minor.patch"
            onChange={(e) => setMinVersion(e.target.value)}
          />
        </div>
        <div className="actions">
          <button type="submit" disabled={save.isPending || !/^\d+\.\d+\.\d+$/.test(minVersion.trim())}>
            Save
          </button>
          <span className="muted">Uploads from older addon versions are refused with a message to update.</span>
        </div>
        {saved && (
          <div className="notice" role="status">
            {saved}
          </div>
        )}
      </form>
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="notice" role="status" data-testid="revert-notice">
          {notice}
        </div>
      )}
      <div className="filters">
        <label htmlFor="addon-source-filter">Show</label>{' '}
        <select
          id="addon-source-filter"
          value={source}
          onChange={(e) => {
            setSource(e.target.value as Filter);
            setPage(1);
          }}
        >
          <option value="">All uploads</option>
          <option value="watcher">Automatic</option>
          <option value="manual">Manual</option>
          <option value="kind:addon">Addon files</option>
          <option value="kind:cache">Cache files</option>
        </select>
      </div>
      {uploads.isPending && <p className="muted">Loading...</p>}
      {uploads.data && (
        <>
          <div className="table-scroll">
            <table className="grid" data-testid="addon-uploads">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">File</th>
                  <th scope="col">Version</th>
                  <th scope="col">Build</th>
                  <th scope="col">How</th>
                  <th scope="col">Status</th>
                  <th scope="col">Read</th>
                  <th scope="col">Artifacts</th>
                  <th scope="col">Touched</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {uploads.data.items.map((u) => (
                  <tr key={u.id} data-testid="addon-upload-row" data-status={u.status} data-kind={u.kind}>
                    <td>{relativeTime(u.createdAt)}</td>
                    <td>{u.uploaderBattletag}</td>
                    <td data-testid="addon-upload-kind">{ADDON_UPLOAD_KIND_LABELS[u.kind]}</td>
                    <td>{u.addonVersion}</td>
                    <td>{u.clientBuild}</td>
                    <td data-testid="addon-upload-source">{sourceLabel(u.source)}</td>
                    <td className={u.status === 'failed' ? 'error' : ''}>
                      {u.status}
                      {u.error && <span className="muted"> - {u.error}</span>}
                    </td>
                    <td>
                      {u.counts.read.npcs} NPCs, {u.counts.read.objects} objects, {u.counts.read.quests} quests,{' '}
                      {u.counts.read.items} items, {u.counts.read.lootEvents} loot
                    </td>
                    <td>
                      {u.counts.artifacts ? (
                        <>
                          +{u.counts.artifacts.added} added, {u.counts.artifacts.updated} updated,{' '}
                          {u.counts.artifacts.skipped} skipped
                        </>
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                    <td>{u.artifactCount}</td>
                    <td>
                      {u.status === 'applied' &&
                        (confirming === u.id ? (
                          <>
                            <button type="button" onClick={() => revert.mutate(u.id)} disabled={revert.isPending}>
                              Confirm revert
                            </button>{' '}
                            <button type="button" onClick={() => setConfirming(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={() => setConfirming(u.id)}>
                            Revert
                          </button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {Math.max(1, Math.ceil(uploads.data.total / PAGE))}
            </span>
            <button type="button" disabled={page * PAGE >= uploads.data.total} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
